import { useEffect, useRef } from 'react'

/**
 * Procedural topographic ground for the public site.
 *
 * Nothing here is an image file: a seeded simplex field is turned into contour
 * lines by marching squares, then a survey grid, crosshair ticks and a dashed
 * section line are drawn over it. That keeps the texture a few kB of code
 * instead of a few hundred kB of PNG, lets it cover any viewport at device
 * resolution without resampling, and means it never tiles visibly.
 *
 * Three properties are deliberate:
 *
 * - **Seeded, so the terrain is the same on every load.** A random field would
 *   redraw differently on every navigation and every resize, which reads as a
 *   glitch rather than as a background.
 * - **Anchored to the page in CSS pixels, not to the canvas.** The field is
 *   sampled in a fixed coordinate space, so a wider window reveals more terrain
 *   rather than stretching what was already drawn — the lines keep their scale
 *   and never smear on a phone rotate or a sidebar toggle.
 * - **Drawn once.** No animation loop, no scroll handler; the layer is `fixed`,
 *   so scrolling is a free composite.
 *
 * Palette is the theme's bone (#efe0d8, index.css) at very low alpha. Crimson
 * stays out of it on purpose: it is rationed to primary actions and live state,
 * and a background that borrows it spends that signal for nothing.
 */

/** Fixed seed — see the note above about redrawing differently every load. */
const SEED = 0x5eed7011

/** Field scale: one full noise feature ≈ this many CSS px. */
const FEATURE_PX = 1080
/** Octaves of the fbm, and how fast they fall off. Four octaves at 0.5 gave
 *  terrain with visible hash in it — contours that zig-zag cell by cell rather
 *  than sweep. Three at 0.45 keeps the ridges and drops the jitter. */
const OCTAVES = 3
const GAIN = 0.45
/** Sampling grid for marching squares. Smaller = smoother lines, more work. */
const CELL_PX = 12
/** Contour spacing in field units; the field lands in about [-1, 1]. */
const LEVEL_STEP = 0.078
/** Every Nth contour is an index contour, drawn brighter — as on a real map. */
const INDEX_EVERY = 5
/** Survey grid pitch. */
const GRID_PX = 132
/** Marker lattice: one candidate crosshair per cell of this size. */
const MARKER_PX = 620
/** A dashed section line every Nth lattice column — ~1240px apart, so a normal
 *  window shows one and a wide one shows two. */
const SECTION_EVERY = 2
/** Backing store is capped at 2× — a 3× phone gains nothing visible here. */
const MAX_DPR = 2

const INK = '239, 224, 216' // bone

/** Deterministic PRNG, so the whole scene follows from SEED alone. */
function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** 2D simplex noise over a permutation shuffled by `rand`. Returns ~[-1, 1]. */
function makeNoise2D(rand: () => number) {
  const p = new Uint8Array(256)
  for (let i = 0; i < 256; i++) p[i] = i
  for (let i = 255; i > 0; i--) {
    const j = (rand() * (i + 1)) | 0
    const t = p[i]; p[i] = p[j]; p[j] = t
  }
  const perm = new Uint8Array(512)
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255]

  const F2 = 0.5 * (Math.sqrt(3) - 1)
  const G2 = (3 - Math.sqrt(3)) / 6
  const gx = [1, -1, 1, -1, 1, -1, 0, 0]
  const gy = [1, 1, -1, -1, 0, 0, 1, -1]

  return (xin: number, yin: number) => {
    const s = (xin + yin) * F2
    const i = Math.floor(xin + s)
    const j = Math.floor(yin + s)
    const t = (i + j) * G2
    const x0 = xin - (i - t)
    const y0 = yin - (j - t)
    const i1 = x0 > y0 ? 1 : 0
    const j1 = x0 > y0 ? 0 : 1
    const x1 = x0 - i1 + G2
    const y1 = y0 - j1 + G2
    const x2 = x0 - 1 + 2 * G2
    const y2 = y0 - 1 + 2 * G2
    const ii = i & 255
    const jj = j & 255

    let n = 0
    let t0 = 0.5 - x0 * x0 - y0 * y0
    if (t0 > 0) {
      const g = perm[ii + perm[jj]] & 7
      t0 *= t0
      n += t0 * t0 * (gx[g] * x0 + gy[g] * y0)
    }
    let t1 = 0.5 - x1 * x1 - y1 * y1
    if (t1 > 0) {
      const g = perm[ii + i1 + perm[jj + j1]] & 7
      t1 *= t1
      n += t1 * t1 * (gx[g] * x1 + gy[g] * y1)
    }
    let t2 = 0.5 - x2 * x2 - y2 * y2
    if (t2 > 0) {
      const g = perm[ii + 1 + perm[jj + 1]] & 7
      t2 *= t2
      n += t2 * t2 * (gx[g] * x2 + gy[g] * y2)
    }
    return 70 * n
  }
}

/**
 * Height field in page coordinates. Four octaves give ridges inside basins;
 * the domain warp is what stops the result from looking like stacked ovals —
 * it bends the rings the way real terrain does.
 */
function makeField(noise: (x: number, y: number) => number) {
  const fbm = (x: number, y: number) => {
    let sum = 0, amp = 1, freq = 1, norm = 0
    for (let o = 0; o < OCTAVES; o++) {
      sum += amp * noise(x * freq, y * freq)
      norm += amp
      amp *= GAIN
      freq *= 2.03
    }
    return sum / norm
  }
  return (px: number, py: number) => {
    const x = px / FEATURE_PX
    const y = py / FEATURE_PX
    const wx = noise(x * 0.6 + 31.4, y * 0.6 - 12.7)
    const wy = noise(x * 0.6 - 8.3, y * 0.6 + 47.1)
    return fbm(x + 0.55 * wx, y + 0.55 * wy)
  }
}

/**
 * One marching-squares pass over the sampled field, appending every crossing
 * segment for `level` to `path`. Saddle cells (5 and 10) are resolved with the
 * cell's centre average, which is what keeps ridges from cross-connecting.
 */
function traceLevel(
  path: Path2D,
  values: Float32Array,
  cols: number,
  rows: number,
  originX: number,
  originY: number,
  level: number,
) {
  const at = (cx: number, cy: number) => values[cy * (cols + 1) + cx]
  const lerp = (a: number, b: number) => (level - a) / (b - a)

  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const v0 = at(cx, cy)          // top-left
      const v1 = at(cx + 1, cy)      // top-right
      const v2 = at(cx + 1, cy + 1)  // bottom-right
      const v3 = at(cx, cy + 1)      // bottom-left

      let code = 0
      if (v0 > level) code |= 1
      if (v1 > level) code |= 2
      if (v2 > level) code |= 4
      if (v3 > level) code |= 8
      if (code === 0 || code === 15) continue

      const x = originX + cx * CELL_PX
      const y = originY + cy * CELL_PX
      const topX = x + CELL_PX * lerp(v0, v1)
      const rightY = y + CELL_PX * lerp(v1, v2)
      const bottomX = x + CELL_PX * lerp(v3, v2)
      const leftY = y + CELL_PX * lerp(v0, v3)
      const top: [number, number] = [topX, y]
      const right: [number, number] = [x + CELL_PX, rightY]
      const bottom: [number, number] = [bottomX, y + CELL_PX]
      const left: [number, number] = [x, leftY]

      const seg = (a: [number, number], b: [number, number]) => {
        path.moveTo(a[0], a[1])
        path.lineTo(b[0], b[1])
      }

      switch (code) {
        case 1: case 14: seg(left, top); break
        case 2: case 13: seg(top, right); break
        case 3: case 12: seg(left, right); break
        case 4: case 11: seg(right, bottom); break
        case 6: case 9:  seg(top, bottom); break
        case 7: case 8:  seg(left, bottom); break
        case 5: case 10: {
          const centre = (v0 + v1 + v2 + v3) / 4
          const joined = code === 5 ? centre > level : centre <= level
          if (joined) { seg(left, top); seg(right, bottom) }
          else { seg(left, bottom); seg(top, right) }
          break
        }
      }
    }
  }
}

export default function TopoBackground({ className = '' }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let frame = 0
    let lastW = 0
    let lastH = 0

    const draw = (w: number, h: number) => {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR)
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      const rand = mulberry32(SEED)
      const noise = makeNoise2D(rand)
      const field = makeField(noise)

      // Sample one cell beyond the viewport on every side so contours run off
      // the edge instead of stopping short of it.
      const originX = -CELL_PX
      const originY = -CELL_PX
      const cols = Math.ceil((w + 2 * CELL_PX) / CELL_PX)
      const rows = Math.ceil((h + 2 * CELL_PX) / CELL_PX)
      const values = new Float32Array((cols + 1) * (rows + 1))
      let vmin = Infinity
      let vmax = -Infinity
      for (let cy = 0; cy <= rows; cy++) {
        for (let cx = 0; cx <= cols; cx++) {
          const v = field(originX + cx * CELL_PX, originY + cy * CELL_PX)
          values[cy * (cols + 1) + cx] = v
          if (v < vmin) vmin = v
          if (v > vmax) vmax = v
        }
      }

      // ── Survey grid, under the terrain ──────────────────────────────────
      ctx.lineWidth = 1
      ctx.strokeStyle = `rgba(${INK}, 0.03)`
      const grid = new Path2D()
      for (let x = 0; x <= w; x += GRID_PX) {
        grid.moveTo(Math.round(x) + 0.5, 0)
        grid.lineTo(Math.round(x) + 0.5, h)
      }
      for (let y = 0; y <= h; y += GRID_PX) {
        grid.moveTo(0, Math.round(y) + 0.5)
        grid.lineTo(w, Math.round(y) + 0.5)
      }
      ctx.stroke(grid)

      // ── Contours ────────────────────────────────────────────────────────
      // Two paths, two strokes: every level is either an index contour or a
      // minor one, so the whole map costs two stroke calls rather than one per
      // level.
      const minor = new Path2D()
      const index = new Path2D()
      // Only the levels this stretch of terrain actually crosses: a level above
      // the highest sample walks every cell to draw nothing.
      const first = Math.ceil(vmin / LEVEL_STEP)
      const last = Math.floor(vmax / LEVEL_STEP)
      for (let k = first; k <= last; k++) {
        traceLevel(k % INDEX_EVERY === 0 ? index : minor, values, cols, rows, originX, originY, k * LEVEL_STEP)
      }
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.strokeStyle = `rgba(${INK}, 0.06)`
      ctx.stroke(minor)
      ctx.strokeStyle = `rgba(${INK}, 0.095)`
      ctx.stroke(index)

      // ── Section lines and survey marks ──────────────────────────────────
      // Positions come from the marker lattice rather than from the viewport,
      // so a resize never slides them across the terrain.
      const firstCol = Math.floor(-MARKER_PX / MARKER_PX)
      const lastCol = Math.ceil(w / MARKER_PX)
      const lastRow = Math.ceil(h / MARKER_PX)

      ctx.strokeStyle = `rgba(${INK}, 0.075)`
      ctx.setLineDash([9, 11])
      const dashed = new Path2D()
      for (let col = firstCol; col <= lastCol; col++) {
        if (((col % SECTION_EVERY) + SECTION_EVERY) % SECTION_EVERY !== 1) continue
        const x = Math.round(col * MARKER_PX + MARKER_PX * 0.46) + 0.5
        dashed.moveTo(x, 0)
        dashed.lineTo(x, h)
      }
      ctx.stroke(dashed)
      ctx.setLineDash([])

      ctx.strokeStyle = `rgba(${INK}, 0.17)`
      const marks = new Path2D()
      for (let col = firstCol; col <= lastCol; col++) {
        for (let row = -1; row <= lastRow; row++) {
          // Per-cell PRNG: each lattice cell decides for itself, so cells that
          // scroll into view later look no different from the ones drawn first.
          const cellRand = mulberry32(SEED ^ (col * 73856093) ^ (row * 19349663))
          if (cellRand() > 0.5) continue
          const onSection = ((col % SECTION_EVERY) + SECTION_EVERY) % SECTION_EVERY === 1
          const x = Math.round(col * MARKER_PX + MARKER_PX * (onSection ? 0.46 : 0.12 + cellRand() * 0.76))
          const y = Math.round(row * MARKER_PX + MARKER_PX * (0.12 + cellRand() * 0.76))
          if (cellRand() < 0.18) {
            const r = 5 + cellRand() * 5
            marks.moveTo(x + r, y)
            marks.arc(x, y, r, 0, Math.PI * 2)
          } else {
            const arm = 5 + cellRand() * 5
            marks.moveTo(x - arm, y + 0.5)
            marks.lineTo(x + arm, y + 0.5)
            marks.moveTo(x + 0.5, y - arm)
            marks.lineTo(x + 0.5, y + arm)
          }
        }
      }
      ctx.stroke(marks)
    }

    const measure = () => {
      const w = canvas.parentElement?.clientWidth || window.innerWidth
      const h = canvas.parentElement?.clientHeight || window.innerHeight
      // Mobile browsers resize the viewport by the height of the URL bar on
      // every scroll direction change. Redrawing the whole field for that is
      // wasted work, and the canvas is full-bleed either way.
      if (w === lastW && Math.abs(h - lastH) < 130) return
      lastW = w
      lastH = h
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => draw(w, h))
    }

    measure()
    const ro = new ResizeObserver(measure)
    if (canvas.parentElement) ro.observe(canvas.parentElement)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(frame)
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  return <canvas ref={canvasRef} aria-hidden className={`block h-full w-full ${className}`} />
}
